import type { GenericArrayOfObjects } from '@/interfaces/api';
import type {
  INovaLoadType,
  INovaDock,
  INovaAppointment,
  INovaPersistedWarehouse,
  INovaSchedule
} from '@/interfaces/nova';

// Shapes of the Neutron payloads: the entity columns, plus the structured values the mappers flatten to JSON text.
export type NovaWarehouseResponse = Omit<
  INovaPersistedWarehouse,
  | 'externalId'
  | 'externalHeadquartersId'
  | 'schedule'
  | 'customApptFieldsTemplate'
  | 'ppeRequirements'
  | 'amenities'
> & {
  schedule: INovaSchedule;
  customApptFieldsTemplate?: GenericArrayOfObjects | null;
  ppeRequirements?: string[];
  amenities?: string[];
};

export type NovaLoadTypeResponse = Omit<INovaLoadType, 'externalId' | 'externalWarehouseId'> & {
  id: string;
  warehouseId?: string;
};

export type NovaDockResponse = Omit<
  INovaDock,
  'externalId' | 'externalWarehouseId' | 'externalLoadTypeIds' | 'schedule' | 'id' | 'warehouseId'
> & {
  id: string;
  warehouseId: string;
  loadTypeIds?: string[];
  schedule?: INovaSchedule | null;
};

export type NovaAppointmentResponse = Omit<
  INovaAppointment,
  | 'id'
  | 'externalId'
  | 'externalWarehouseId'
  | 'externalDockId'
  | 'externalLoadTypeId'
  | 'externalCustomerId'
  | 'externalCompanyName'
  | 'tags'
  | 'email'
  | 'ccEmails'
  | 'firstName'
  | 'lastName'
  | 'customFields'
  | 'isNovaExport'
> & {
  id: string;
  dock: { warehouseId: string };
  dockId: string;
  loadTypeId?: string;
  userId: string;
  user?: {
    email?: string;
    firstName?: string;
    lastName?: string;
    company?: { name?: string };
  };
  tags?: string[];
  ccEmails?: string[];
  customFields?: GenericArrayOfObjects | null;
};

export function mapWarehouse(warehouse: NovaWarehouseResponse): INovaPersistedWarehouse {
  return {
    ...warehouse,
    externalId: warehouse.id,
    schedule: JSON.stringify(warehouse.schedule),
    customApptFieldsTemplate: warehouse.customApptFieldsTemplate
      ? JSON.stringify(warehouse.customApptFieldsTemplate)
      : null,
    ppeRequirements: JSON.stringify(warehouse.ppeRequirements),
    amenities: JSON.stringify(warehouse.amenities)
  };
}

export function mapLoadType(loadType: NovaLoadTypeResponse): INovaLoadType {
  return {
    ...loadType,
    externalId: loadType.id,
    externalWarehouseId: loadType.warehouseId ?? ''
  };
}

export function mapDock(dock: NovaDockResponse): INovaDock {
  return {
    ...dock,
    externalId: dock.id,
    externalWarehouseId: dock.warehouseId,
    externalLoadTypeIds: JSON.stringify(dock.loadTypeIds),
    schedule: dock.schedule ? JSON.stringify(dock.schedule) : null
  };
}

export function mapAppointment(appointment: NovaAppointmentResponse): INovaAppointment {
  const tags = appointment.tags?.length
    ? Array.from(new Set([...appointment.tags, 'Imported'])).join(',')
    : 'Imported';
  return {
    ...appointment,
    externalId: appointment.id,
    externalWarehouseId: appointment.dock.warehouseId,
    externalLoadTypeId: appointment.loadTypeId ?? '',
    externalDockId: appointment.dockId,
    externalCustomerId: appointment.userId,
    externalCompanyName: appointment.user?.company?.name,
    isNovaExport: true,
    ccEmails: appointment.ccEmails ?? [],
    tags,
    email: appointment.user?.email ?? appointment.ccEmails?.[0] ?? '',
    customFields: appointment.customFields ? JSON.stringify(appointment.customFields) : undefined,
    firstName: appointment.user?.firstName ?? '',
    lastName: appointment.user?.lastName ?? ''
  };
}
